import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import './NotificationCenter.css';

const NotificationCenter = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    totalPages,
    page,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearRead,
    loadMore,
    fetchNotifications
  } = useNotifications();

  const [filter, setFilter] = useState('all'); // 'all' or 'unread'
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    fetchNotifications(1, filter === 'unread');
  }, [filter, fetchNotifications]);

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification._id);
    }

    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleMarkAllRead = async () => {
    if (window.confirm('Mark all notifications as read?')) {
      await markAllAsRead();
    }
  };

  const handleClearRead = async () => {
    if (window.confirm('Delete all read notifications?')) {
      await clearRead();
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      booking_new: '📅',
      booking_confirmed: '✅',
      booking_cancelled: '❌',
      payment_received: '💰',
      trip_reminder: '⏰',
      trip_updated: '🔄',
      application_new: '📝',
      application_status: '📋',
      system: 'ℹ️'
    };
    return icons[type] || '🔔';
  };

  const getPriorityClass = (priority) => {
    return `priority-${priority}`;
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredNotifications = notifications.filter(notif => {
    if (selectedType !== 'all' && notif.type !== selectedType) {
      return false;
    }
    return true;
  });

  const notificationTypes = [
    { value: 'all', label: 'All' },
    { value: 'booking_new', label: 'New Bookings' },
    { value: 'booking_confirmed', label: 'Confirmations' },
    { value: 'trip_reminder', label: 'Reminders' },
    { value: 'payment_received', label: 'Payments' },
    { value: 'system', label: 'System' }
  ];

  return (
    <div className="notification-center">
      <div className="notification-center-header">
        <h1>Notifications</h1>
        {unreadCount > 0 && (
          <span className="unread-count-badge">
            {unreadCount} unread
          </span>
        )}
      </div>

      <div className="notification-controls">
        <div className="notification-filters">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
            onClick={() => setFilter('unread')}
          >
            Unread
          </button>

          <select
            className="type-select"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            {notificationTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="notification-actions">
          {unreadCount > 0 && (
            <button
              className="action-btn"
              onClick={handleMarkAllRead}
            >
              Mark All Read
            </button>
          )}
          {notifications.some(n => n.read) && (
            <button
              className="action-btn danger"
              onClick={handleClearRead}
            >
              Clear Read
            </button>
          )}
        </div>
      </div>

      <div className="notifications-container">
        {loading && page === 1 ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🔕</span>
            <h2>No notifications</h2>
            <p>
              {filter === 'unread'
                ? "You're all caught up!"
                : 'Notifications will appear here'}
            </p>
          </div>
        ) : (
          <>
            <div className="notifications-list">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`notification-card ${notification.read ? 'read' : 'unread'} ${getPriorityClass(notification.priority)}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-card-icon">
                    {getNotificationIcon(notification.type)}
                  </div>

                  <div className="notification-card-content">
                    <div className="notification-card-header">
                      <h3>{notification.title}</h3>
                      {!notification.read && <div className="unread-indicator"></div>}
                    </div>

                    <p className="notification-card-message">
                      {notification.message}
                    </p>

                    <div className="notification-card-footer">
                      <span className="notification-time">
                        {getTimeAgo(notification.createdAt)}
                      </span>
                      {notification.priority === 'high' && (
                        <span className="priority-badge high">High Priority</span>
                      )}
                      {notification.priority === 'urgent' && (
                        <span className="priority-badge urgent">Urgent</span>
                      )}
                    </div>
                  </div>

                  <div className="notification-card-actions">
                    {!notification.read && (
                      <button
                        className="mark-read-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification._id);
                        }}
                        title="Mark as read"
                      >
                        ✓
                      </button>
                    )}
                    <button
                      className="delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification._id);
                      }}
                      title="Delete"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {page < totalPages && (
              <div className="load-more-container">
                <button
                  className="load-more-btn"
                  onClick={loadMore}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;
